import { Overlay, OverlayRef, ConnectedPosition } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Directive,
  ElementRef,
  HostListener,
  inject,
  input,
} from '@angular/core';

/** Bulle rendue dans l'overlay CDK. Purement présentielle : aucun rôle ARIA. */
@Component({
  selector: 'cce-tooltip-bulle',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `{{ texte() }}`,
  styles: [
    `
      :host {
        display: block;
        /*
         * L'ÉCART À LA CIBLE SE POSE ICI, en logique — pas par l'offsetX du CDK, qui est un
         * décalage PHYSIQUE : en RTL il poussait la bulle vers la droite, donc SOUS le rail qu'elle
         * est censée longer. margin-inline-start vaut dans les deux directions, puisque la bulle
         * s'ouvre toujours du côté du contenu.
         * (Pas de backtick dans ce commentaire : il fermerait le gabarit de styles.)
         */
        margin-inline-start: 8px;
        max-inline-size: 240px;
        padding: 6px 10px;
        border-radius: var(--radius-md, 6px);
        background: var(--color-brand-900);
        color: #f5f2ea;
        font-size: 12.5px;
        font-weight: 600;
        line-height: 1.35;
        box-shadow: 0 6px 20px rgba(11, 42, 74, 0.28);
        pointer-events: none;
      }
    `,
  ],
})
export class TooltipBulle {
  readonly texte = input('');
}

/**
 * Infobulle de survol/focus pour un contrôle réduit à son icône (rail de navigation).
 *
 * <p><b>Pourquoi pas `title=`.</b> L'attribut natif attend ~500 ms, ne se thème pas, et son rendu
 * bidi est incohérent d'un système à l'autre — inacceptable pour des libellés arabes. Le même
 * constat avait déjà écarté `title` pour l'aide contextuelle (`app-aide`).
 *
 * <p><b>Accessibilité.</b> La bulle est un pur décor : elle ne porte aucun rôle et n'est jamais
 * référencée par `aria-describedby`. Le nom accessible du lien reste porté par son libellé, masqué
 * visuellement mais TOUJOURS présent dans le DOM (`clip-path`, jamais `display:none`) — un lecteur
 * d'écran lit donc le menu réduit exactement comme le menu déployé, et la bulle ne lui ajoute ni
 * ne lui retire rien.
 *
 * <p><b>RTL.</b> Les positions CDK `start`/`end` sont résolues par la `Directionality` du document :
 * la bulle s'ouvre vers le contenu (à gauche en arabe, à droite en français) sans code de direction.
 *
 * <p>Usage : `<a [cceTooltip]="libelle" [cceTooltipActif]="estEnRail()">`.
 */
@Directive({
  selector: '[cceTooltip]',
  standalone: true,
})
export class CceTooltip {
  /** Texte de la bulle. Vide ⇒ rien ne s'ouvre. */
  readonly texte = input.required<string>({ alias: 'cceTooltip' });
  /** Interrupteur : à `false`, la directive reste inerte (menu déployé, tactile). */
  readonly actif = input(true, { alias: 'cceTooltipActif' });

  private readonly overlay = inject(Overlay);
  private readonly hote = inject(ElementRef<HTMLElement>);
  private overlayRef?: OverlayRef;
  private minuteur?: ReturnType<typeof setTimeout>;

  /** Délai d'ouverture : évite d'allumer une bulle en traversant le rail à la souris. */
  private static readonly DELAI_MS = 140;

  private static readonly POSITIONS: readonly ConnectedPosition[] = [
    { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' },
    { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center' },
  ];

  constructor() {
    inject(DestroyRef).onDestroy(() => this.fermer());
  }

  @HostListener('mouseenter')
  @HostListener('focus')
  ouvrirDifferee(): void {
    if (!this.actif() || !this.texte()) return;
    this.annulerMinuteur();
    this.minuteur = setTimeout(() => this.ouvrir(), CceTooltip.DELAI_MS);
  }

  @HostListener('mouseleave')
  @HostListener('blur')
  @HostListener('click')
  @HostListener('document:keydown.escape')
  fermer(): void {
    this.annulerMinuteur();
    this.overlayRef?.dispose();
    this.overlayRef = undefined;
  }

  private ouvrir(): void {
    if (this.overlayRef) return;
    this.overlayRef = this.overlay.create({
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.hote)
        .withPositions([...CceTooltip.POSITIONS])
        .withPush(false),
      // La bulle suit l'élément si le menu défile, et disparaît s'il sort de l'écran.
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false,
    });
    const ref = this.overlayRef.attach(new ComponentPortal(TooltipBulle));
    ref.setInput('texte', this.texte());
  }

  private annulerMinuteur(): void {
    if (this.minuteur !== undefined) {
      clearTimeout(this.minuteur);
      this.minuteur = undefined;
    }
  }
}
